import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import type { Partner } from '@zuruflex/types';
import { formatPhone } from '@zuruflex/utils';
import { Badge, Button, DataTable, ErrorState, PageHeader, type ColumnDef } from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useTableLabels } from '../lib/table-labels.js';

const STATUS_VARIANT = {
  APPROVED: 'success',
  PENDING: 'warning',
  SUSPENDED: 'danger',
  CLOSED: 'default',
} as const;

const KYC_VARIANT = {
  VERIFIED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
  UNVERIFIED: 'outline',
} as const;

/**
 * The queue. Approving used to happen here, on a row, which made it a
 * decision taken without opening anything — so the action is now a way in
 * to the paperwork rather than a verdict on it.
 */
export function PartnersPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.listPartners({ perPage: 100 }),
  });

  const columns = useMemo<ColumnDef<Partner>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: t('common:field.name'),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link
              to={`/partners/${row.original.id}`}
              className="font-medium underline-offset-2 hover:underline"
            >
              {row.original.displayName}
            </Link>
            {/* Our own fleet is a partner row too; saying so stops anyone
                wondering why it cannot be suspended. */}
            {row.original.isFirstParty ? (
              <Badge variant="outline">{t('partners.firstParty')}</Badge>
            ) : null}
            {/* Terms waiting on Zuruflex. On the name rather than in a column
                of its own, because it is true of a handful of rows at a
                time and a mostly-empty column is a column nobody reads. */}
            {row.original.termsPendingReview ? (
              <Badge variant="info">{t('partners.termsWaiting')}</Badge>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'kind',
        header: t('common:field.type'),
        cell: ({ row }) => <Badge>{t(`common:partnerKind.${row.original.kind}`)}</Badge>,
      },
      {
        accessorKey: 'status',
        header: t('common:field.status'),
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status]}>
            {t(`common:partnerStatus.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        /*
         * The column the queue is actually worked from. A PENDING partner
         * whose documents are UNVERIFIED is waiting on themselves; one
         * whose documents are PENDING is waiting on us, and only the second
         * is a job.
         */
        accessorKey: 'kycStatus',
        header: t('partners.kyc'),
        cell: ({ row }) => (
          <Badge variant={KYC_VARIANT[row.original.kycStatus]}>
            {t(`common:kycStatus.${row.original.kycStatus}`)}
          </Badge>
        ),
      },
      {
        accessorKey: 'contactPhone',
        header: t('common:field.phone'),
        cell: ({ row }) => (
          <span className="font-mono">{formatPhone(row.original.contactPhone)}</span>
        ),
      },
      {
        accessorKey: 'commissionBps',
        header: t('common:field.commission'),
        cell: ({ row }) =>
          `${(row.original.commissionBps / 100).toFixed(row.original.commissionBps % 100 ? 2 : 0)}%`,
      },
      {
        accessorKey: 'submittedAt',
        header: t('partners.waiting'),
        cell: ({ row }) =>
          row.original.submittedAt ? (
            <span className="text-sm">{formatDateTimeIn(row.original.submittedAt, locale)}</span>
          ) : (
            <span className="text-sm text-text-muted">—</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button asChild size="sm" variant="ghost">
            <Link to={`/partners/${row.original.id}`}>{t('partners.review')}</Link>
          </Button>
        ),
      },
    ],
    [t, locale],
  );

  /** What is genuinely on Zuruflex's desk, as opposed to merely unfinished. */
  const waiting = data?.items.filter((partner) => partner.submittedAt !== null).length ?? 0;

  return (
    <>
      <PageHeader
        title={t('partners.title')}
        description={
          data
            ? `${t('partners.count', { count: data.total })}${waiting ? ` · ${t('partners.waitingCount', { count: waiting })}` : ''}`
            : t('common:state.loading')
        }
      />

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          labels={tableLabels}
          columns={columns}
          data={data?.items ?? []}
          isLoading={isPending}
          searchColumn="displayName"
          searchPlaceholder={t('partners.searchPlaceholder')}
          emptyMessage={t('partners.none')}
        />
      )}
    </>
  );
}
